// @flow
import React from "react";
import { compose } from "recompose";
import Highcharts from "highcharts/highstock";
import HC_exporting from "highcharts/modules/exporting";
import { Modal, withModal } from "@quantum/ui";
import "./highcharts-modal.css";
import {
  Chart,
  HighchartsChart,
  HighchartsProvider,
  Legend,
  LineSeries,
  Tooltip,
  XAxis,
  YAxis,
} from "react-jsx-highcharts";
import { useI18n } from "@quantum/i18n";
import { useSelector } from "react-redux";
import { getSeparadorDecimal } from "../../../state";

type Props = {
  dados: Object,
  // Exemplo de formato dos dados
  //
  // dados = [ {
  //   coluna: "id da coluna",
  //   datas: ["01/01/2022, 02/01/2022"],
  //   formatador: "percentage",
  //   medida: "Retorno",
  //   valores: [1, 2],
  //   valoresFormatados: ["R$ 1,00", "R$ 2,00"],
  // }, { ...próxima série...} ]

  show: boolean,
  onHide: Function,
};

const enhance = compose(withModal("HighchartsModal"));

const HighchartsModal = ({ show, onHide, dados }: Props) => {
  const { translate } = useI18n();
  const separadorDecimal = useSelector(getSeparadorDecimal);

  Highcharts.setOptions({
    lang: {
      contextButtonTitle: translate("Comum.FazerDownloadImagem"),
      downloadPDF: translate("Comum.DownloadPDF"),
      downloadJPEG: translate("Comum.DownloadJPEG"),
      downloadPNG: translate("Comum.DownloadPNG"),
      decimalPoint: separadorDecimal,
      thousandsSep: "",
    },
  });

  HC_exporting(Highcharts);

  const contextButtonIcon =
    "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";

  const exporting = {
    enabled: true,
    filename: "GraficoAxisExportado",
    buttons: {
      contextButton: {
        menuItems: ["downloadPNG", "downloadPDF", "downloadJPEG"],
        symbol: `url(data:image/svg+xml;base64,${contextButtonIcon})`,
        verticalAlign: "top",
        height: 29,
        width: 33,
        symbolX: 24,
        symbolY: 22,
        align: "left",
        // x: 80,
        // y: 5,
      },
    },

    chartOptions: {
      credits: {
        enabled: true,
        href: "http://www.quantumfundos.com.br/",
        text: "Quantum Axis",
        style: {
          fontSize: "10px",
        },
      },
    },
  };

  const delay = millis =>
    new Promise((resolve, reject) => {
      setTimeout(_ => resolve(), millis);
    });

  const chart = {
    events: {
      // O load do chart tem alguma incompatibilidade com a montagem da modal
      // e com o componente BS, esse trecho gera um tempo entre a aplicação
      // da animação de entrada / composição do modal e o reflow
      // para a div que redimensiona o gráfico com tamanho correto
      load: async function() {
        try {
          var chart = this;
          await delay(50);
          chart.reflow();
        } catch {
          // O highcharts tenta executar esse código novamente após
          // o export, mas o objeto chart ainda está sendo recalculado
          // é seguro ignorar esse erro
        }
      },
    },
  };

  const yLabels = {
    formatter: function() {
      const value = this.value;
      const decimals = value > 10 ? 0 : 4;

      return Highcharts.numberFormat(value, decimals);
    },
  };

  return (
    <Modal
      mountOnShow
      transition
      className="HighChartsModal"
      header={onHide => (
        <div className="HighChartsModal__header">
          <div className="HighChartsModal__header-titulo">
            {translate("configuracaoRelatorio.resultado.grafico.titulo")}
          </div>
          <div className="HighChartsModal__close" onClick={onHide}>
            &times;
          </div>
        </div>
      )}
      show={show}
      onHide={onHide}
    >
      <HighchartsProvider
        Highcharts={Highcharts}
        className="HighChartsModal__chart"
      >
        <HighchartsChart exporting={exporting} chart={chart}>
          <Chart />

          <Legend layout="vertical" align="center" verticalAlign="top" />
          <Tooltip
            padding={10}
            hideDelay={250}
            shape="square"
            formatter={function() {
              const medida = this.point.series.name;
              const indiceSerie = dados.findIndex(
                dado => dado.medida === medida
              );
              return dados[indiceSerie].valoresFormatados[this.point.x];
            }}
          />

          <XAxis type="datetime" categories={dados && dados[0].datas}>
            <XAxis.Title></XAxis.Title>
          </XAxis>

          <YAxis labels={yLabels}>
            {dados &&
              dados
                .filter(dado => dado.coluna !== "DATA")
                .map((dado, index) => (
                  <LineSeries
                    key={index}
                    name={dado.medida}
                    data={dado.valores}
                    connectNulls={false}
                  />
                ))}
          </YAxis>
        </HighchartsChart>
      </HighchartsProvider>
    </Modal>
  );
};

export default enhance(HighchartsModal);
